import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { seoHead } from "@/lib/seo";
import { useCart, formatUsd } from "@/lib/cart";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { paymentsConfigured } from "@/lib/stripe";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import type { CheckoutLineInput } from "@/utils/payments.functions";

export const Route = createFileRoute("/checkout")({
  head: () =>
    seoHead({
      path: "/checkout",
      title: "Checkout — Coffman Cove Lodging Shop",
      description:
        "Place your Coffman Cove Lodging shop order. Everything ships directly to your home in the lower 48 states.",
      extraMeta: [{ name: "robots", content: "noindex, nofollow" }],
    }),
  component: CheckoutPage,
});

const inputClass =
  "mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ember)]";

function Field({
  label,
  name,
  type = "text",
  required = true,
  autoComplete,
  className = "",
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  className?: string;
}) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="text-muted-foreground">
        {label}
        {required && <span className="text-[var(--color-ember)]"> *</span>}
      </span>
      <input
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        className={inputClass}
      />
    </label>
  );
}

function CheckoutPage() {
  const navigate = useNavigate();
  const { lines, count, subtotal, shipping, total, hydrated, clear } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [payment, setPayment] = useState<{
    email: string;
    lines: CheckoutLineInput[];
    shippingCents: number;
  } | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || count === 0) return;
    const form = e.currentTarget;
    const fd = new FormData(form);
    const get = (k: string) => String(fd.get(k) ?? "").trim();

    setSubmitting(true);
    try {
      // Generate the id client-side: anon has INSERT but no SELECT on
      // shop_orders, so we cannot ask PostgREST to return the new row.
      const newId = crypto.randomUUID();
      const { error } = await supabase
        .from("shop_orders")
        .insert({
          id: newId,
          status: "pending",
          customer_name: get("customer_name"),
          email: get("email"),
          phone: get("phone") || null,
          address_line1: get("address_line1"),
          address_line2: get("address_line2") || null,
          city: get("city"),
          state: get("state"),
          postal_code: get("postal_code"),
          notes: get("notes") || null,
          items: lines.map((l) => ({
            id: l.product.id,
            name: l.product.name,
            variant: Object.keys(l.variant).length ? l.variant : null,
            variant_label: l.variantLabel || null,
            qty: l.qty,
            unit_price_cents: Math.round(l.product.price * 100),
          })),
          subtotal_cents: Math.round(subtotal * 100),
          shipping_cents: Math.round(shipping * 100),
          total_cents: Math.round(total * 100),
        });

      if (error) throw error;
      setPayment({
        email: get("email"),
        shippingCents: Math.round(shipping * 100),
        lines: lines.map((l) => ({
          name: l.product.name,
          description: l.variantLabel || undefined,
          unitAmountCents: Math.round(l.product.price * 100),
          quantity: l.qty,
        })),
      });
      setOrderId(newId);
      clear();
    } catch (err) {
      console.error("[checkout] order submit failed", err);
      toast.error("We couldn't place that order. Please try again or call the lodge.");
    } finally {
      setSubmitting(false);
    }
  }

  if (orderId && payment) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-16">
        <PaymentTestModeBanner />
        <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-[var(--color-ember)]">
          Order {orderId.slice(0, 8).toUpperCase()}
        </p>
        <h1 className="mt-2 font-display text-4xl">Payment</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Pay securely by card below. Your order is held at the lodge until payment clears.
        </p>
        <div className="mt-8">
          {paymentsConfigured() ? (
            <StripeEmbeddedCheckout
              orderId={orderId}
              email={payment.email}
              lines={payment.lines}
              shippingCents={payment.shippingCents}
              returnUrl={`${window.location.origin}/checkout/return?order=${orderId}`}
            />
          ) : (
            <p className="rounded-xl border border-border bg-card p-5 text-sm text-muted-foreground">
              Card payments aren't switched on yet. We've saved your order and will email
              you a secure payment link within one business day.
            </p>
          )}
        </div>
      </div>
    );
  }


  if (hydrated && count === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-20 text-center">
        <h1 className="font-display text-3xl">Your cart is empty.</h1>
        <p className="mt-3 text-muted-foreground">
          Add something from the lodge shop and we'll get it shipped to you.
        </p>
        <button
          onClick={() => navigate({ to: "/shop" })}
          className="mt-6 rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)]"
        >
          Browse the shop
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--color-ember)]">
        Lodge Shop
      </p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl">Checkout</h1>
      <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
        Tell us where to send it, then pay securely by card on the next step.
      </p>

      <div className="mt-8 grid lg:grid-cols-[1fr_20rem] gap-10">
        <form onSubmit={handleSubmit} className="rounded-xl border border-border bg-card p-5 sm:p-6">
          <h2 className="font-display text-xl">Shipping details</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <Field label="Full name" name="customer_name" autoComplete="name" />
            <Field label="Email" name="email" type="email" autoComplete="email" />
            <Field label="Phone" name="phone" type="tel" required={false} autoComplete="tel" />
            <div className="hidden sm:block" />
            <Field
              label="Street address"
              name="address_line1"
              autoComplete="address-line1"
              className="sm:col-span-2"
            />
            <Field
              label="Apt / suite"
              name="address_line2"
              required={false}
              autoComplete="address-line2"
              className="sm:col-span-2"
            />
            <Field label="City" name="city" autoComplete="address-level2" />
            <div className="grid grid-cols-2 gap-4">
              <Field label="State" name="state" autoComplete="address-level1" />
              <Field label="ZIP" name="postal_code" autoComplete="postal-code" />
            </div>
          </div>

          <label className="mt-4 block text-sm">
            <span className="text-muted-foreground">Order notes (sizes, colors, pickup)</span>
            <textarea
              name="notes"
              rows={3}
              className={inputClass}
              placeholder="e.g. Hoodie in XL, tee in medium — or hold for pickup at the lodge."
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? "Placing order…" : `Continue to payment — ${formatUsd(total)}`}
          </button>
          <p className="mt-3 text-xs text-muted-foreground">
            Shipping to the lower 48 states only. Lodge guests can note pickup above and we'll
            waive shipping.
          </p>
        </form>

        <aside className="h-fit rounded-xl border border-border bg-card p-5">
          <h2 className="font-display text-xl">Order summary</h2>
          <ul className="mt-4 space-y-3 text-sm">
            {lines.map(({ key, product, qty, variantLabel }) => (
              <li key={key} className="flex justify-between gap-3">
                <span className="min-w-0">
                  <span className="block truncate">{product.name}</span>
                  {variantLabel && (
                    <span className="block truncate text-muted-foreground">
                      {variantLabel}
                    </span>
                  )}
                  <span className="text-muted-foreground">× {qty}</span>
                </span>
                <span className="shrink-0 font-semibold">
                  {formatUsd(product.price * qty)}
                </span>
              </li>
            ))}
          </ul>
          <dl className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Subtotal</dt>
              <dd className="font-semibold">{formatUsd(subtotal)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Shipping</dt>
              <dd className="font-semibold">
                {shipping === 0 ? "Free" : formatUsd(shipping)}
              </dd>
            </div>
            <div className="flex justify-between border-t border-border pt-3 text-base">
              <dt className="font-semibold">Total</dt>
              <dd className="font-semibold">{formatUsd(total)}</dd>
            </div>
          </dl>
          <Link
            to="/cart"
            className="mt-5 block text-center text-xs uppercase tracking-wider text-muted-foreground hover:text-[var(--color-ember)]"
          >
            Edit cart
          </Link>
        </aside>
      </div>
    </div>
  );
}
